// Czech (cs) admin translations.
//
// Scope: backfills D2 "Build-your-own-bouquet" keys (admin form + custom-bouquet
// order-line block). Everything else inherits English so the admin remains
// usable for cs users until a full translation pass lands.
//
// Vocabulary aligned with the customer-facing builder in
// `moonflowers-app/src/i18n/cs.json` (Tier S/M/L/XL, "Sestavení", "Přizpůsobení",
// "Vlastní kytice"). Do not reinvent terms here without updating the mobile app too.
import customEnglishMessages from './en';

const customCzechMessages = {
  ...customEnglishMessages,
  resources: {
    ...customEnglishMessages.resources,
    'manual-orders': {
      ...customEnglishMessages.resources['manual-orders'],
      fields: {
        ...customEnglishMessages.resources['manual-orders'].fields,
        customRequest: 'Individuální požadavek',
        customBudget: 'Individuální rozpočet',
        paidAt: 'Platba',
        paid: 'Zaplaceno',
        unpaid: 'Nezaplaceno',
        event: 'Událost',
      },
    },
    events: {
      name: 'Událost |||| Události',
      fields: {
        title: 'Název',
        description: 'Popis',
        deliveryStartDate: 'Začátek doručení',
        deliveryEndDate: 'Konec doručení',
        maxPerDay: 'Max. objednávek za den',
        branchId: 'Pobočka',
        deliveryFee: 'Poplatek za doručení',
        currency: 'Měna',
        allowedCategoryIds: 'Povolené kategorie',
        allowedProductIds: 'Povolené kytice',
        contactPhone: 'Telefon příjemce',
        contactData: 'Jméno příjemce',
        defaultAddress: 'Výchozí doručovací adresa',
        isActive: 'Aktivní',
        createdAt: 'Vytvořeno',
        updatedAt: 'Aktualizováno',
      },
      sections: {
        pricing: 'Ceny',
        catalogScope: 'Rozsah katalogu',
        contact: 'Kontakt a doručení',
      },
      helpers: {
        titleFallback: 'Český (hlavní) název. Zobrazí se českým hostům a slouží jako záloha pro jazyky bez překladu.',
        descriptionFallback: 'Český (hlavní) popis. Zobrazí se českým hostům a slouží jako záloha pro jazyky bez překladu.',
        maxPerDay: 'Datum se stane nedostupným, jakmile je na něj tolik objednávek. Výchozí 1.',
        branch: 'Pobočka, která objednávku vyřídí. Povinné — hostovské objednávky ji zdědí.',
        deliveryFee: 'Přičítá se ke každé objednávce. Nechte prázdné pro globální nastavení.',
        catalogScope:
          'Nechte výběr kytic prázdný pro nabídku všech aktivních kytic z katalogu. Jinak hosté uvidí jen vybrané kytice.',
        defaultAddress: 'Předvyplní se hostovi, ale lze ji při objednávce upravit.',
      },
      copyLink: {
        sectionLabel: 'Odkaz pro hosty',
        label: 'Veřejná URL pro hosty',
        helper: 'Tuto URL zakódujte do QR kódu vytištěného pro událost.',
        button: 'Kopírovat odkaz',
        copied: 'Odkaz pro hosty zkopírován',
        copyFailed: 'Kopírování selhalo — označte pole a zkopírujte ručně.',
      },
    },
  },
  app: {
    ...customEnglishMessages.app,
    board: {
      ...customEnglishMessages.app.board,
      weddingEvent: 'Svatební událost',
    },
    order: {
      ...customEnglishMessages.app.order,
      eventDetails: 'Detaily události',
    },
    mobileOrder: {
      ...customEnglishMessages.app.mobileOrder,
      updateStatus: 'Změnit stav',
      status: {
        pending: 'Čeká',
        confirmed: 'Potvrzeno',
        preparing: 'Připravuje se',
        ready: 'Připraveno',
        delivering: 'Doručuje se',
        delivered: 'Doručeno',
        cancelled: 'Zrušeno',
      },
      // D2.5 — custom-bouquet detail block
      customBouquet: {
        tag: 'VLASTNÍ',
        fallbackTitle: 'Vlastní kytice',
        tier: 'Tier %{code}',
        stems: 'Květiny',
        stemsSubtotal: 'Mezisoučet za květiny',
        lineTotal: 'Položka',
        assembly: 'Sestavení',
        customization: 'Přizpůsobení',
        customizationSubtotal: 'Mezisoučet za přizpůsobení',
        snapshotTotal: 'Celkem za vlastní kytici',
        snapshotMissing:
          'Snapshot není k dispozici. Endpoint detailu objednávky musí přiložit `customCompositions[%{id}]`, aby se zobrazily úplné detaily.',
        wrap: 'Balení',
        ribbon: 'Stuha',
        box: 'Krabice',
        card: 'Přání',
        none: 'Žádné',
      },
    },
    bouquetBuilder: {
      title: 'Nastavení konstruktoru kytic',
      menu: 'Konstruktor kytic',
      enabled: 'Konstruktor zapnut',
      enabledHint:
        'Hlavní vypínač. Když je vypnutý, obrazovka konstruktoru i dlaždice v katalogu jsou skryté.',
      live: 'Zapnuto',
      off: 'Vypnuto',
      minOrderTitle: 'Minimální hodnota objednávky',
      minOrderInvalid: 'Minimální hodnota objednávky musí být nezáporné celé číslo',
      tiersTitle: 'Velikostní tiery (rozsahy stonků + poplatek za sestavení)',
      tiersHint:
        'Tiery musí pokrýt 1..∞ bez mezer. Pouze poslední tier může mít ∞ jako maximum stonků.',
      saved: 'Nastavení konstruktoru kytic uložena',
      lastUpdated: 'Naposledy aktualizováno: %{date}',
      tier: {
        code: 'Kód',
        minStems: 'Min. stonků',
        maxStems: 'Max. stonků',
        feeKc: 'Poplatek (CZK)',
        add: 'Přidat tier',
        infinityHint: 'Přepnout ∞ (bez horní hranice) pro poslední tier',
      },
      category: {
        title: 'Kategorie — překlady + fotky',
        slug: 'Interní název',
        image: 'Náhledová fotka',
        noImage: 'Bez obrázku',
        titles: 'Zobrazované názvy podle jazyka',
        uploadButton: 'Nahrát obrázek',
        uploadHint: 'JPG/PNG, ≤ 5 MB',
        uploadPending:
          'Nahrávání obrázků čeká na podporu v backendu (sloupec + endpoint)',
        uploadOk: 'Obrázek nahrán',
        saved: 'Kategorie uloženy',
        nothingChanged: 'Žádné změny kategorií k uložení',
        missingCs: 'Český (cs) název je povinný pro: ',
        empty: "Nenalezeny žádné kategorie 'Flowers –%'. Zkontrolujte konvenci pojmenování.",
        dirty: '%{n} neuložených',
        clean: 'Všechny změny uloženy',
        backendPending:
          'Nahrávání obrázků a ukládání překladů vyžadují rozšíření backendu: (1) spustit migraci 20260524120000 (categories.image_url), (2) rozšířit UpdateCategoryDto o pole `translations` a `image_url`, (3) přidat POST /categories/:id/image (multipart) podle vzoru z campaigns.',
      },
    },
  },
};

export default customCzechMessages;
